'use client';

import { createContext, useContext, useEffect, useState } from "react";
import { Event, Pagination } from "@/app/lib/generated/graphql-types";
import { getEvents } from "@/app/features/sites/events/data-access/queries";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Direction } from "@/app/features/sites/events/types/types";

interface EventListContextType {
    isLoading: boolean;
    error: string | null;
    events: Event[];
    direction: Direction;
    pagination: Pagination;
    setPage: (page: number) => void;
    setPageSize: (pageSize: number) => void;
}

const EventListContext = createContext<EventListContextType | undefined>(undefined);

export function useEventList() {
    const context = useContext(EventListContext);
    if (context === undefined) {
        throw new Error("useEventList must be used within an EventListContextProvider");
    }
    return context;
}

interface EventListContextProviderProps {
    children: React.ReactNode;
    eventId?: string;
    query?: string;
    page: number;
    pageSize: number;
    refDate: string;
    direction: Direction;
}

export function EventListContextProvider({
    children,
    eventId,
    query,
    page,
    pageSize,
    refDate,
    direction,
}: EventListContextProviderProps) {

    const { push } = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [events, setEvents] = useState<Event[]>([]);
    const [pagination, setPagination] = useState<Pagination>({
        page: 0,
        pageSize: 0,
        pageCount: 0,
        total: 0
    })

    const setPage = (nextPage: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('page', String(nextPage));
        params.set('pageSize', String(pagination.pageSize || pageSize));
        push(`${pathname}?${params.toString()}`);
    }

    const setPageSize = (nextPageSize: number) => {
        // Reset to first page when page size changes to keep UX predictable
        const params = new URLSearchParams(searchParams);
        params.set('page', '1');
        params.set('pageSize', String(nextPageSize));
        push(`${pathname}?${params.toString()}`);
    }

    useEffect(() => {
        const fetchEvents = async () => {
            setIsLoading(true);
            const { data, error } = await getEvents({
                eventId,
                query,
                refDate,
                direction,
                pagination: {
                    page, 
                    pageSize
                }
            })

            if (error) {
                setError(error.message)
                return;
            } else if (!data) {
                setError("Something went wrong")
                return;
            }

            setEvents(data!.events)
            setPagination(data!.pagination)
        }

        fetchEvents().finally(() => setIsLoading(false));
    }, [query, refDate, direction, page, pageSize, eventId]);


    return (
        <EventListContext.Provider
            value={{
                isLoading,
                error,
                events,
                pagination,
                setPage,
                setPageSize,
                direction
            }}
        >
            {children}
        </EventListContext.Provider>
    );
}
